import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { resolveAssetUrl } from "@/lib/media";

import fashionImg from "@/assets/case-studies/fashion-brand.webp";
import beautyImg from "@/assets/case-studies/beauty-brand.webp";
import homeLivingImg from "@/assets/case-studies/home-living.webp";

const fallbackStudies = [
  {
    title: "Fashion Brand Replatform",
    industry: "Ecommerce",
    short_description: "Migrated legacy platform to Shopify Plus with custom theme and AI-powered search.",
    featured_image: fashionImg,
    desktop_screenshot: null as string | null,
    slug: "#",
  },
  {
    title: "D2C Beauty Launch",
    industry: "Ecommerce",
    short_description: "Launched custom Shopify store with subscription model and Klaviyo integration.",
    featured_image: beautyImg,
    desktop_screenshot: null as string | null,
    slug: "#",
  },
  {
    title: "B2B Wholesale Portal",
    industry: "Web",
    short_description: "Built B2B portal with custom pricing, bulk ordering, and ERP integration.",
    featured_image: homeLivingImg,
    desktop_screenshot: null as string | null,
    slug: "#",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

export function CaseStudies() {
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);

  const { data: featuredWork } = useQuery({
    queryKey: ["featured-work"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work")
        .select("title, slug, short_description, featured_image, desktop_screenshot, industry")
        .eq("featured", true)
        .eq("status", "published")
        .order("sort_order", { ascending: true })
        .limit(4);

      if (error) throw error;
      return data;
    },
  });

  const studies = featuredWork && featuredWork.length > 0 ? featuredWork : fallbackStudies;

  return (
    <section id="portfolio" className="py-16 lg:py-32 bg-background text-foreground">
      <div className="container px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
            <p className="text-sm uppercase tracking-[0.25em] text-primary font-medium mb-4">{t("Portfolio")}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">{t("Selected Work from")} <span className="text-gradient">{t("Kuwait & Beyond")}</span></h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
            <Link to={localePath("/work")} className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline">
              {t("View all projects")} <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
          {studies.map((study, i) => {
            const img = resolveAssetUrl(study.desktop_screenshot || study.featured_image);
            const content = (
              <motion.div key={study.slug || study.title} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} custom={i}>
                <div className="group block">
                  {img && (
                    <div className="aspect-[16/10] overflow-hidden rounded-xl bg-foreground/5 mb-5">
                      <img src={img} alt={study.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out" loading="lazy" />
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-sans font-semibold text-base lg:text-[20px] text-foreground group-hover:text-primary transition-colors duration-200">{study.title}</h3>
                      {study.industry && <p className="text-sm text-muted-foreground mt-1">{study.industry}</p>}
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-primary flex-shrink-0 mt-1 transition-colors duration-200" />
                  </div>
                </div>
              </motion.div>
            );

            return study.slug && study.slug !== "#" ? (
              <Link key={study.slug} to={localePath(`/work/${study.slug}`)} className="block">
                {content}
              </Link>
            ) : (
              <div key={study.title}>{content}</div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
